import React from 'react';
import { inject, observer } from 'mobx-react';
import { withTheme } from 'styled-components';
import PropTypes from 'prop-types';
import { Flex, Link, Tag } from 'core/components';
import LabelValue from 'app/views/metrics/components/LabelValue';
import AlertSeverityBars from 'app/views/alerting/components/AlertSeverityBars';
import { formatDateTime, DEFAULT_DATETIME_FORMAT_WITH_SECONDS } from 'core/util/dateUtils';
import { ALERT_CLEAR_REASONS } from '@kentik/ui-shared/alerting/constants';
import storeLoader from 'app/stores/storeLoader';

/**
 * This component displays the following information about an Alert:
 * - Tenant link (if tenant properties are specified)
 * - Alert ID
 * - Severity
 * - Alert state
 * - Ack state
 * - Alert start and end
 * - Clear status
 */

@withTheme
@storeLoader('$users.collection')
@inject('$auth', '$users')
@observer
export default class AlertOverview extends React.Component {
  static propTypes = {
    // This must be an AlertModel instance
    alertModel: PropTypes.object.isRequired,

    // Set to true to display the alert ID as a link to the alert's detail page
    linkToDetail: PropTypes.bool,

    // Customize the width of the labels
    labelWidth: PropTypes.number
  };

  static defaultProps = {
    linkToDetail: undefined,
    labelWidth: 150
  };

  render() {
    const { $auth, $users, alertModel, linkToDetail, labelWidth = 150 } = this.props;
    const clearedByUserId = alertModel.clearCause && alertModel.manualClearCause && alertModel.manualClearUserId;
    const clearedByUser = clearedByUserId && $users.collection.get(clearedByUserId)?.get?.('user_full_name');

    return (
      <>
        <LabelValue
          label="ID"
          value={
            linkToDetail ? (
              <Link to={alertModel.alertDetailLink} blank>
                {alertModel.id}
              </Link>
            ) : (
              alertModel.id
            )
          }
          labelWidth={labelWidth}
        />
        <LabelValue
          label="Severity"
          value={
            <Flex alignItems="center" gap="4px">
              <AlertSeverityBars severity={alertModel.severity} showLabel />
            </Flex>
          }
          labelWidth={labelWidth}
        />
        <LabelValue
          label="Alert State"
          labelWidth={labelWidth}
          value={
            <Flex alignItems="center" gap="4px">
              <Tag intent={alertModel.stateColor} width="80px" textAlign="center">
                {alertModel.stateLabel}
              </Tag>
            </Flex>
          }
        />
        {alertModel.clearCause && !alertModel.unspecifiedClearCause && (
          <LabelValue
            label="Reason Alert Cleared"
            labelWidth={labelWidth}
            value={ALERT_CLEAR_REASONS[alertModel.clearCause]}
          />
        )}
        {(clearedByUserId || clearedByUser) && (
          <LabelValue label="Manual Clear By" labelWidth={labelWidth} value={clearedByUser || clearedByUserId} />
        )}
        <LabelValue label="Ack State" labelWidth={labelWidth} value={alertModel.ackStateLabel} />
        <LabelValue
          label="Start Time"
          value={
            <>
              {formatDateTime(alertModel.startTime, DEFAULT_DATETIME_FORMAT_WITH_SECONDS)} ({$auth.userTimezone})
            </>
          }
          labelWidth={labelWidth}
        />
        {alertModel.eventEndTime && (
          <LabelValue
            label="Event End Time"
            value={`${formatDateTime(alertModel.eventEndTime, DEFAULT_DATETIME_FORMAT_WITH_SECONDS)} (${$auth.userTimezone})`}
            labelWidth={labelWidth}
          />
        )}
        <LabelValue
          label="Clear Time"
          labelWidth={labelWidth}
          value={
            alertModel.endTime
              ? `${formatDateTime(alertModel.endTime, DEFAULT_DATETIME_FORMAT_WITH_SECONDS)}  (${$auth.userTimezone})`
              : 'Currently Active'
          }
        />
      </>
    );
  }
}
